// Single source of truth for satellite imagery providers, shared by the Konva
// editor backdrop (satellite_layer.js), the Leaflet "Показать на карте" modal
// (map_controller.js), and the provider switcher UI (plot_display_controller).
//
// `tileSlug` MUST match a key in TileProxyController::PROVIDERS (Ruby) — the
// client always talks to our same-origin /tiles/<slug>/{z}/{x}/{y} proxy, never
// upstream directly.
//
// `maxZoom` is the source's published worldwide maximum zoom. Esri World
// Imagery is z0–19 globally; z20+ exists only in selected high-density areas
// and is unreliable elsewhere (requesting it returns "tile not found" → gaps).
// We cap tile *fetch* zoom at maxZoom and let the renderer scale the last sharp
// tile up when the user pinches in further — mild blur beats missing tiles.
//
// `attribution` is a list of {text, href?} the consumers render in their own
// markup (DOM overlay for Konva, HTML string for Leaflet). `href` is optional:
// entries without one are the underlying imagery data providers, which Esri's
// terms require us to *name* but which have no single URL to link to. Both Esri
// and MapTiler ToS require this to stay visible whenever their tiles are shown.

export const SATELLITE_PROVIDERS = {
  esri: {
    id: "esri",
    label: "Esri (чётче)",
    tileSlug: "esri-satellite",
    maxZoom: 19,
    // Esri World Imagery canonical credit names the data providers; only the
    // "Esri" mark links out. Maxar/Earthstar are data sources, not a link.
    attribution: [
      { text: "© Esri", href: "https://www.esri.com/" },
      { text: "Maxar, Earthstar Geographics" },
    ],
  },
  maptiler: {
    id: "maptiler",
    label: "MapTiler",
    tileSlug: "maptiler-satellite",
    maxZoom: 20,
    attribution: [
      { text: "© MapTiler", href: "https://www.maptiler.com/copyright/" },
      { text: "© OpenStreetMap", href: "https://www.openstreetmap.org/copyright" },
    ],
  },
}

// Esri is sharper than MapTiler across the rural RU/Caucasus ИЖС market, so it
// is the default. MapTiler stays available as a fallback / when its imagery is
// fresher for a given plot.
export const DEFAULT_SATELLITE_PROVIDER = "esri"

export function isSatelliteProvider(id) {
  return Object.prototype.hasOwnProperty.call(SATELLITE_PROVIDERS, id)
}

// Always returns a valid provider config — falls back to the default for
// unknown/stale ids (e.g. a draft saved before a provider was renamed).
export function resolveSatelliteProvider(id) {
  return SATELLITE_PROVIDERS[id] || SATELLITE_PROVIDERS[DEFAULT_SATELLITE_PROVIDER]
}

export const satelliteTileUrlTemplate = (id) =>
  `/tiles/${resolveSatelliteProvider(id).tileSlug}/{z}/{x}/{y}`
